<template>
    <main class="content">
        <header class="content-header">
            <section class="content-header-section">
                <router-link :to="`/${ $options.module }`" class="button button-back">
                    {{ $t('general.form.buttons.cancel') }}
                </router-link>
            </section>
            <section class="content-header-section">
                <button
                    type="button"
                    class="button button-blue"
                    @click="actionPublish"
                    :disabled="!valid"
                >
                    {{ $t('general.form.buttons.send') }}
                </button>
            </section>
        </header>
        <div v-if="element" class="content-form">
            <input-radio
                field="notificationType"
                :element="element"
                :label="$t(`pages.${ this.$options.module }.form.labels.notificationType`)"
                :errors="errors"
                :currentLanguage="currentLanguage"
                :options="[
                    { value: TYPE_ALL_USERS, title: $t(`pages.${ this.$options.module }.form.values.allUsers`)},
                    { value: TYPE_TARGET_COUNTRIES, title: $t(`pages.${ this.$options.module }.form.values.targetCountries`)}
                ]"
            />
            <template v-if="element.notificationType === TYPE_TARGET_COUNTRIES">
              <select-countries
                :element="element"
                field="countryIds"
                :label="$t(`pages.${ this.$options.module }.form.labels.countries`)"
                :placeholder="$t(`pages.${ this.$options.module }.form.placeholders.countries`)"
                :currentLanguage="currentLanguage"
            />
            <select-regions
                v-if="element.countryIds.length <= 1"
                :disabled="element.countryIds.length === 0"
                v-model="regioncodes"
                :country-id="element.countryIds.length ? element.countryIds[0].id : null"
                :label="$t(`pages.${ this.$options.module }.form.labels.regions`)"
                :placeholder="$t(`pages.${ this.$options.module }.form.placeholders.regions`)"
            />
            </template>
            <input-checkbox
                v-model="element.travelstatuses"
                :label="$t(`pages.${ this.$options.module }.form.labels.travelstatuses`)"
                :errors="errors"
                :options="travelstatusesOptions"
            />
            <input-i18n
                field="title"
                :label="$t(`pages.${ this.$options.module }.form.labels.title`)"
                :placeholder="$t(`pages.${ this.$options.module }.form.placeholders.title`)"
                :locales="element.locales"
                :errors="errors"
                :currentLanguage="currentLanguage"
                @actionChangeLang="actionChangeLang"
                :maxlength="65"
            />
            <input-textarea
                field="description"
                :label="$t(`pages.${ this.$options.module }.form.labels.description`)"
                :placeholder="$t(`pages.${ this.$options.module }.form.placeholders.description`)"
                :locales="element.locales"
                :errors="errors"
                :currentLanguage="currentLanguage"
                :subtext="$t(`pages.${ this.$options.module }.form.subtext.description`)"
                @actionChangeLang="actionChangeLang"
                :maxlength="1000"
            />
            <input-i18n
                field="urlText"
                :label="$t(`pages.${ this.$options.module }.form.labels.urlText`)"
                :placeholder="$t(`pages.${ this.$options.module }.form.placeholders.urlText`)"
                :locales="element.locales"
                :errors="errors"
                :currentLanguage="currentLanguage"
                @actionChangeLang="actionChangeLang"
            />
            <input-i18n
                field="url"
                :label="$t(`pages.${ this.$options.module }.form.labels.url`)"
                :placeholder="$t(`pages.${ this.$options.module }.form.placeholders.url`)"
                :locales="element.locales"
                :errors="errors"
                :currentLanguage="currentLanguage"
                @actionChangeLang="actionChangeLang"
                :note="$t(`pages.${ this.$options.module }.form.subtext.url`)"
            />
        </div>
        <pop-down
            v-if="pop.active"
            :class="pop.className"
            @close="popClose"
        >
            {{ pop.message }}
        </pop-down>
        <modal
            v-if="submitOpen"
            :title="$t(`pages.${ $options.module }.submitPopup.title`)"
            :message="$t(`pages.${ $options.module }.submitPopup.description`)"
            :cancelShow="true"
            :confirmLabel="$t(`pages.${ $options.module }.submitPopup.success`)"
            @cancel="submitCancel"
            @confirm="submitConfirm"
        />
    </main>
</template>

<script>
import PopDownMixin from '@/components/PopDownMixin';
import FormMixin from '@/components/form/FormMixin';
import Modal from '@/components/Modal.vue';
import languages from '@/config/languages';

const TYPE_ALL_USERS = 2;
export const TYPE_TARGET_COUNTRIES = 1;
export const TRAVELSTATUS_TRAVELLING = 1;

export const travelstatuses = [
    { value: 0, titleKey: 'planned' },
    { value: TRAVELSTATUS_TRAVELLING, titleKey: 'travelling' },
    { value: 2, titleKey: 'abroad' },
];

export default {
    mixins: [FormMixin, PopDownMixin],
    name: 'NotificationsForm',
    module: 'notifications',
    components: {
        Modal,
    },
    emptyElement: {
        notificationType: TYPE_TARGET_COUNTRIES,
        countryIds: [],
        travelstatuses: [],
        locales: {
            de: {
                title: '',
                url: '',
                description: '',
                urlText: '',
            },
            it: {
                title: '',
                url: '',
                description: '',
                urlText: '',
            },
            fr: {
                title: '',
                url: '',
                description: '',
                urlText: '',
            },
            en: {
                title: '',
                url: '',
                description: '',
                urlText: '',
            },
        },
    },
    data() {
        return {
            submitOpen: false,
            regioncodes: [],
        };
    },
    async created() {
        await this.$store.dispatch('countries/list');
    },
    computed: {
        valid() {
            if (!this.element) {
                return false;
            }

            if (this.element.notificationType === TYPE_TARGET_COUNTRIES && this.element.countryIds.length === 0) {
                return false;
            }

            if (this.element.travelstatuses.length === 0) {
                return false;
            }

            return languages.every((lang) => {
                if (this.element.locales[lang].title.length === 0) return false;
                if (this.element.locales[lang].description.length === 0) return false;
                return true;
            });
        },
        TYPE_TARGET_COUNTRIES: () => TYPE_TARGET_COUNTRIES,
        TYPE_ALL_USERS: () => TYPE_ALL_USERS,
        travelstatusesOptions() {
            return travelstatuses.map((status) => ({
                value: status.value,
                title: this.$t(`pages.${ this.$options.module }.form.values.${ status.titleKey }`),
            }));
        },
    },
    methods: {
        actionPublish() {
            this.submitOpen = true;
        },
        submitCancel() {
            this.submitOpen = false;
        },
        submitConfirm() {
            if (this.element.countryIds.length === 1) {
                this.element.regioncodes = this.regioncodes;
            }
            this.submitOpen = false;
            this.actionSave();
        },
        actionBackToList() {
            const message = 'success_create_notification';
            this.$router.push({ name: this.$options.module, query: { m: message } });
        },
        actionValidate() {
            return this.valid;
        },
    },
};
</script>
